import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';
import WelcomeHero from './components/WelcomeHero';
import CompanyCard from './components/CompanyCard';
import HelpSection from './components/HelpSection';
import SecurityBadges from './components/SecurityBadges';

export default function CompanySelectionHub() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  // ADDED: Display error message from navigation state (if redirected from registration form)
  useEffect(() => {
    const stateError = location?.state?.error;
    if (stateError) {
      setError(stateError);
      // Clear the error from history state
      window.history?.replaceState({}, document.title);
    }
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await companyService?.getAll();
      
      // Transform company data to include logo path and proper structure
      const transformedData = data?.map(company => ({
        id: company?.id,
        name: company?.name,
        logo: company?.logo_url || company?.logoUrl, // Handle both snake_case and camelCase
        logoAlt: `${company?.name} company logo`,
        description: company?.description,
        accentColor: 'bg-blue-500',
        iconColor: 'var(--color-primary)',
        highlights: [
          'Professional onboarding experience',
          'Structured learning programs',
          'Career development opportunities'
        ]
      }));
      
      setCompanies(transformedData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleCompanySelect = (companyId) => {
    // ENHANCED: Clear any previous registration data and set new company
    localStorage.removeItem('registrationFormData');
    localStorage.removeItem('registrationFormStep');
    localStorage.setItem('registrationCompanyId', companyId);
    
    // Navigate to registration form with company ID
    navigate('/progressive-registration-form', { state: { companyId } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading companies...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header with conditional authentication display */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">CorperConnect</h1>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <span className="text-gray-700">Welcome, {profile?.fullName || user?.email}</span>
                
                {/* Admin Access Tab - Only visible for admin role */}
                {profile?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin-dashboard')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
                      />
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                      />
                    </svg>
                    Admin Panel
                  </button>
                )}

                <button
                  onClick={handleSignOut}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/signin')}
                  className="px-4 py-2 text-blue-600 hover:text-blue-800 transition"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* WelcomeHero with background image and custom text */}
      <WelcomeHero />
      
      {/* Companies Grid */}
      <section className="container mx-auto px-4 py-3">
        <h2 className="text-3xl font-bold text-center mb-8">Select Your Company</h2>
        <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-8">
          Choose the organization where you'll be serving. Each company has prepared a tailored onboarding experience to help you settle in smoothly and make the most of your service year.
        </p>
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {companies?.map((company, index) => (
            <CompanyCard
              key={company?.id}
              company={company}
              index={index}
              onSelect={() => handleCompanySelect(company?.id)}
            />
          ))}
        </div>
      </section>
      
      {/* HelpSection */}
      <HelpSection />
      
      {/* SecurityBadges */}
      <SecurityBadges />
    </div>
  );
}